import { Moment } from 'moment';
import capitalize from 'lodash/capitalize';
import dayjs, { Dayjs } from 'dayjs';

// antd pickers pin their dates to today's UTC offset once the dayjs timezone plugin is loaded
// (getNow() returns dayjs().tz()). Rebuilding from the wall-clock fields gives the picked date's own offset.
export const toPickedWallClock = (date: Dayjs) => dayjs(date.format('YYYY-MM-DDTHH:mm:ss'));

export const combinePickedDateAndTime = (date: Dayjs, time: string) => dayjs(`${date.format('YYYY-MM-DD')}T${time}`);

const MOCK_INTL = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formatMessage: (options: any) => ""
}
export const getDisabledDate = (current, options: {notAfterToday?: boolean, notBeforeToday?: boolean} = {}) => {
  let finalResult = false;
  if (options.notAfterToday){
    finalResult = finalResult || (current && current > dayjs().endOf('day'))
  }
  if (options.notBeforeToday){
    finalResult = finalResult || (current && current <= dayjs().endOf('day'))
  }
  return finalResult
}



export const DateRanges = (intl = MOCK_INTL, currentDiff, showLast = true, showNext = false) => {

  const days = [7,14,30,90,180];
  let ranges = [];

  if (showLast) {
    const lastRanges = days.map((day) => ({
      dateDiff: day,
      value: [dayjs().add(-1*day, 'days'), dayjs()],
      label: <div className={currentDiff === day ? 'active-date-diff' : ''}>{intl.formatMessage({
        id: `utils.date.lastXDays`,
        defaultMessage: `Last {day} Days`,
      }, {
        day
      })}</div>
    }));
    ranges = [...ranges, ...lastRanges];
  }

  if (showNext) {
    const nextRanges = days.map((day) => ({
      dateDiff: -1*day,
      value: [dayjs(), dayjs().add(day, 'days')],
      label: <div className={currentDiff === -day ? 'active-date-diff' : ''}>{intl.formatMessage({
        id: `utils.date.nextXDays`,
        defaultMessage: `Next {day} Days`,
      }, {
        day
      })}</div>
    }));
    ranges = [...ranges, ...nextRanges];
  }

  return ranges;

};


// quickDiff is the relative-preset day count stored in filters.date
// (positive = "Last N days", negative = "Next N days"); resolves it to a
// fresh {start, end} in ms epoch, or null if it matches no known preset.
export const getQuickDiffDateRange = (quickDiff: number) => {
  const quickDate = DateRanges(undefined, quickDiff, true, true)
    .find(range => range.dateDiff === quickDiff);
  if (!quickDate) return null;
  const [start, end] = quickDate.value;
  return { start: start.unix() * 1000, end: end.unix() * 1000 };
};

export function daysForLocale(localeName = 'de-DE', weekday: Intl.DateTimeFormatOptions["weekday"] = 'long') {
  const format = new Intl.DateTimeFormat(localeName, { weekday }).format;
  return [...Array(7).keys()]
    .map((day) => format(new Date(Date.UTC(2021, 5, day))));
}

export function getWeekDayNameForLocale(day: number, localeName = 'de-DE', weekday: Intl.DateTimeFormatOptions["weekday"] = 'long') {
  const days = daysForLocale(localeName, weekday)
  return days[day]
}

export function transformDayNameToLocale(dayName: string, localeName = 'de-DE', weekday: Intl.DateTimeFormatOptions["weekday"] = 'long') {
  function transform(){
    const days = daysForLocale(localeName, weekday).map(dayName => dayName.toLowerCase())
    if (days.includes(dayName.toLowerCase())) {
      return dayName
    }
    const englishDays = daysForLocale('en-US', weekday).map(dayName => dayName.toLowerCase())
    const englishDayIndex = englishDays.indexOf(dayName.toLowerCase())
    if (englishDayIndex >= 0) {
      return getWeekDayNameForLocale(englishDayIndex, localeName, weekday)
    }
    return dayName
  }
  return capitalize(transform())
}

export function formatDateForAPIs(date: Moment){
  return date.format("YYYY-MM-DD")
}


export const setDateAndHour = (dateObject, timeObject) => {
  // Create a Day.js instance from the date object
  const dateInstance = dayjs(dateObject);

  // Extract the hour, minute, and second from the time object
  const timeInstance = dayjs(timeObject);

  // Combine the date and time
  const combinedDateTime = dateInstance
    .hour(timeInstance.hour()) // Set the hour
    .minute(timeInstance.minute()) // Set the minute
    .second(timeInstance.second()); // Set the second

  return combinedDateTime;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * `date` as an ISO 8601 string in the browser's local time with that date's own UTC offset,
 * e.g. 2026-11-24T00:00:00+01:00.
 *
 * Date, time and offset all come from the same instant: the UTC date (toISOString) next to
 * the local time put midnight on the previous day, and today's offset (instead of the date's)
 * put a summer date an hour off in winter.
 */
export function formatToISOStringWithOffset(date: Date) {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);

  const day = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;

  return `${day}T${time}${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}
